import React, { useEffect, useMemo, useState } from 'react';

import { toProjectDateOnly, toUtcDeadline } from 'src/utils/date';
import { useProjectSettingCtx } from 'src/features/projects/contexts/ProjectSettingContext';

import type { ChangeOrderDetail, ChangeOrderStatus } from 'src/types/changeOrder.type';
import { useProjectMembers } from 'src/features/risks/hooks/useProjectMembers'; // ✅ Risk의 멤버 훅 재사용
import {
    useChangeOrder,
    useCreateChangeOrder,
    useDeleteChangeOrder,
    useSetChangeOrderStatus,
    useUpdateChangeOrder,
} from '../../hooks/useChangeOrders';

const STATUS_OPTIONS: ChangeOrderStatus[] = [
    'DRAFT',
    'DISCUSSING',
    'WAITING_COUNTERPART',
    'READY_TO_SIGN',
    'APPROVED',
    'REJECTED',
    'APPLIED',
    'CLOSED',
];

// function toDateInput(v: any): string | null {
//     if (!v) return null;
//     if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
//     const s = typeof v === 'string' ? v : v instanceof Date ? v.toISOString() : String(v);
//     const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
//     return m ? m[1] : null;
// }

type Draft = {
    title: string;
    description: string;
    reasonCode: string;

    ownerUserId: string | null; // null = 서버 자동(등록자)
    status: ChangeOrderStatus;

    costDelta: string;
    currency: string;
    scheduleDeltaDays: string;

    nextAction: string;
    nextDueDate: string | null;
    blockedReason: string;

    decisionNote: string;
    evidenceUrl: string; // 1차는 단일 URL만
};

export default function ChangeOrderDrawer({
    open,
    mode,
    projectId,
    contractId,
    changeOrderId,
    onClose,
    canEdit,
}: {
    open: boolean;
    mode: 'create' | 'edit';
    projectId: string;
    contractId: string;
    changeOrderId: string | null;
    onClose: () => void;
    canEdit?: boolean;
}) {
    const { setting } = useProjectSettingCtx();
    const zone = setting?.timeZone ?? 'Asia/Seoul';

    const membersQ = useProjectMembers(projectId, open);
    const members = membersQ.data ?? [];

    const detailQ = useChangeOrder(
        projectId,
        contractId,
        mode === 'edit' ? changeOrderId : null,
        open && mode === 'edit',
    );

    const createMut = useCreateChangeOrder(projectId, contractId);
    const updateMut = useUpdateChangeOrder(projectId, contractId);
    const deleteMut = useDeleteChangeOrder(projectId, contractId);
    const setStatusMut = useSetChangeOrderStatus(projectId, contractId);

    const [draft, setDraft] = useState<Draft | null>(null);
    const [statusNote, setStatusNote] = useState('');

    const loaded: ChangeOrderDetail | null = useMemo(() => (detailQ.data as any) ?? null, [detailQ.data]);

    useEffect(() => {
        if (!open) return;

        if (mode === 'create') {
            setDraft({
                title: '',
                description: '',
                reasonCode: '',
                ownerUserId: null, // ✅ 자동(등록자)
                status: 'DRAFT',
                costDelta: '',
                currency: '',
                scheduleDeltaDays: '',
                nextAction: '',
                nextDueDate: null,
                blockedReason: '',
                decisionNote: '',
                evidenceUrl: '',
            });
            setStatusNote('');
            return;
        }

        if (mode === 'edit' && loaded) {
            setDraft({
                title: loaded.title ?? '',
                description: (loaded as any).description ?? '',
                reasonCode: (loaded as any).reasonCode ?? '',
                ownerUserId: loaded.ownerUserId ?? null,
                status: loaded.status ?? 'DRAFT',
                costDelta: loaded.costDelta == null ? '' : String(loaded.costDelta),
                currency: loaded.currency ?? '',
                scheduleDeltaDays: loaded.scheduleDeltaDays == null ? '' : String(loaded.scheduleDeltaDays),
                nextAction: loaded.nextAction ?? '',
                // nextDueDate: toDateInput(loaded.nextDueDate),
                nextDueDate: loaded.nextDueDate ? toProjectDateOnly(loaded.nextDueDate as any, zone) : null,
                blockedReason: (loaded as any).blockedReason ?? '',
                decisionNote: (loaded as any).decisionNote ?? '',
                evidenceUrl: Array.isArray((loaded as any).evidenceLinks)
                    ? String((loaded as any).evidenceLinks?.[0]?.url ?? '')
                    : '',
            });
            setStatusNote('');
        }
    }, [open, mode, loaded, zone]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    const onField = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((p) => (p ? { ...p, [k]: v } : p));

    const canSave = !!draft?.title.trim();

    const saveCreate = async () => {
        if (!draft) return;
        if (!canSave) return;

        await createMut.mutateAsync({
            title: draft.title.trim(),
            description: draft.description.trim() || null,
            reasonCode: draft.reasonCode.trim() || null,

            ownerUserId: draft.ownerUserId, // null이면 서버 자동

            costDelta: draft.costDelta.trim() ? Number(draft.costDelta) : null,
            currency: draft.currency.trim() || null,
            scheduleDeltaDays: draft.scheduleDeltaDays.trim() ? Number(draft.scheduleDeltaDays) : null,

            nextAction: draft.nextAction.trim() || null,
            // nextDueDate: draft.nextDueDate || null,
            nextDueDate: draft.nextDueDate ? toUtcDeadline(draft.nextDueDate, zone) : null,
            blockedReason: draft.blockedReason.trim() || null,

            evidenceLinks: draft.evidenceUrl.trim() ? [{ url: draft.evidenceUrl.trim() }] : undefined,
        });

        onClose();
    };

    const saveEdit = async () => {
        if (!draft || !changeOrderId) return;
        if (!canSave) return;

        await updateMut.mutateAsync({
            changeOrderId,
            patch: {
                title: draft.title.trim(),
                description: draft.description.trim() || null,
                reasonCode: draft.reasonCode.trim() || null,

                ownerUserId: draft.ownerUserId,

                costDelta: draft.costDelta.trim() ? Number(draft.costDelta) : null,
                currency: draft.currency.trim() || null,
                scheduleDeltaDays: draft.scheduleDeltaDays.trim() ? Number(draft.scheduleDeltaDays) : null,

                nextAction: draft.nextAction.trim() || null,
                // nextDueDate: draft.nextDueDate || null,
                nextDueDate: draft.nextDueDate ? toUtcDeadline(draft.nextDueDate, zone) : null,

                blockedReason: draft.blockedReason.trim() || null,

                decisionNote: draft.decisionNote.trim() || null,
                evidenceLinks: draft.evidenceUrl.trim() ? [{ url: draft.evidenceUrl.trim() }] : undefined,
            },
        });
    };

    const changeStatus = async () => {
        if (!draft || !changeOrderId) return;

        await setStatusMut.mutateAsync({
            changeOrderId,
            body: {
                status: draft.status,
                note: statusNote.trim() || null,

                nextAction: draft.nextAction.trim() || null,
                // nextDueDate: draft.nextDueDate || null,
                nextDueDate: draft.nextDueDate ? toUtcDeadline(draft.nextDueDate, zone) : null,

                blockedReason: draft.blockedReason.trim() || null,

                // APPROVED로 바꾸는 경우, decidedAt을 자동으로 찍어도 되지만(2차), 1차는 note/decisionNote로만
                decisionNote: draft.decisionNote.trim() || null,
                evidenceLinks: draft.evidenceUrl.trim() ? [{ url: draft.evidenceUrl.trim() }] : undefined,
            },
        });

        setStatusNote('');
    };

    const onDelete = async () => {
        if (!changeOrderId) return;
        const ok = window.confirm('삭제할까요?');
        if (!ok) return;
        await deleteMut.mutateAsync(changeOrderId);
        onClose();
    };

    const saving =
        createMut.isPending ||
        updateMut.isPending ||
        deleteMut.isPending ||
        setStatusMut.isPending ||
        detailQ.isLoading;

    return (
        <div className="fixed inset-0 z-50">
            {/* overlay */}
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />

            {/* panel */}
            <div className="absolute right-0 top-0 h-full w-full max-w-[560px] bg-white shadow-2xl border-l flex flex-col">
                <div className="px-4 py-3 border-b flex items-center justify-between">
                    <div className="text-sm font-medium">
                        {mode === 'create' ? 'New Change Order' : 'Change Order'}
                        {mode === 'edit' && changeOrderId ? (
                            <span className="ml-2 text-xs text-gray-500">{changeOrderId}</span>
                        ) : null}
                    </div>
                    <div className="flex items-center gap-2">
                        {mode === 'edit' && (
                            <button
                                className="text-xs rounded px-2.5 py-1.5 border bg-white hover:bg-gray-50"
                                onClick={onDelete}
                                disabled={saving || !canEdit}
                            >
                                Delete
                            </button>
                        )}
                        <button
                            className="text-xs rounded px-2.5 py-1.5 bg-gray-900 text-white hover:bg-gray-700"
                            onClick={onClose}
                        >
                            Close
                        </button>
                    </div>
                </div>

                {!draft ? (
                    <div className="p-4 text-sm text-gray-500">Loading…</div>
                ) : (
                    <div className="p-4 space-y-4 overflow-auto">
                        <label className="block space-y-1">
                            <div className="text-xs text-gray-500">Title *</div>
                            <input
                                className="w-full rounded border p-2 text-sm"
                                value={draft.title}
                                onChange={(e) => onField('title', e.target.value)}
                                disabled={saving}
                            />
                        </label>

                        <label className="block space-y-1">
                            <div className="text-xs text-gray-500">Description</div>
                            <textarea
                                className="w-full rounded border p-2 text-sm min-h-[90px]"
                                value={draft.description}
                                onChange={(e) => onField('description', e.target.value)}
                                disabled={saving}
                            />
                        </label>

                        <div className="grid grid-cols-2 gap-2">
                            <label className="block space-y-1">
                                <div className="text-xs text-gray-500">Reason Code</div>
                                <input
                                    className="w-full rounded border p-2 text-sm"
                                    value={draft.reasonCode}
                                    onChange={(e) => onField('reasonCode', e.target.value)}
                                    disabled={saving}
                                />
                            </label>

                            <label className="block space-y-1">
                                <div className="text-xs text-gray-500">Status</div>
                                <select
                                    className="w-full rounded border p-2 text-sm bg-white"
                                    value={draft.status}
                                    onChange={(e) => onField('status', e.target.value as ChangeOrderStatus)}
                                    disabled={saving || mode === 'create'}
                                    title={mode === 'create' ? '생성 후 상태 변경을 권장' : ''}
                                >
                                    {STATUS_OPTIONS.map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </select>
                                {mode === 'create' ? (
                                    <div className="text-[11px] text-gray-500">
                                        생성 후 상태 업데이트로 전환을 기록하세요.
                                    </div>
                                ) : null}
                            </label>
                        </div>

                        {/* Owner */}
                        <label className="block space-y-1">
                            <div className="text-xs text-gray-500">Owner</div>
                            <select
                                className="w-full rounded border p-2 text-sm bg-white"
                                value={draft.ownerUserId ?? '__AUTO__'}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    onField('ownerUserId', v === '__AUTO__' ? null : v);
                                }}
                                disabled={saving}
                            >
                                <option value="__AUTO__">자동(등록자)</option>
                                {members.map((m) => (
                                    <option key={m.userId} value={m.userId}>
                                        {(m.user?.name || m.user?.email || m.userId).trim()}{' '}
                                        {m.role ? `(${m.role})` : ''}
                                    </option>
                                ))}
                            </select>
                            <div className="text-[11px] text-gray-500">
                                미선택 시 서버가 “등록자=책임자”로 설정합니다.
                            </div>
                        </label>

                        {/* Impact */}
                        <div className="grid grid-cols-3 gap-2">
                            <label className="block space-y-1 col-span-2">
                                <div className="text-xs text-gray-500">Δ Cost</div>
                                <input
                                    className="w-full rounded border p-2 text-sm"
                                    value={draft.costDelta}
                                    onChange={(e) => onField('costDelta', e.target.value)}
                                    disabled={saving}
                                    placeholder="e.g. 12000000 (can be -)"
                                />
                            </label>
                            <label className="block space-y-1">
                                <div className="text-xs text-gray-500">Currency</div>
                                <input
                                    className="w-full rounded border p-2 text-sm"
                                    value={draft.currency}
                                    onChange={(e) => onField('currency', e.target.value.toUpperCase())}
                                    disabled={saving}
                                    placeholder="KRW"
                                />
                            </label>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                            <label className="block space-y-1">
                                <div className="text-xs text-gray-500">Δ Days</div>
                                <input
                                    className="w-full rounded border p-2 text-sm"
                                    value={draft.scheduleDeltaDays}
                                    onChange={(e) => onField('scheduleDeltaDays', e.target.value)}
                                    disabled={saving}
                                    placeholder="e.g. 7 (can be -)"
                                />
                            </label>

                            <label className="block space-y-1">
                                <div className="text-xs text-gray-500">Next Due Date</div>
                                <input
                                    type="date"
                                    className="w-full rounded border p-2 text-sm"
                                    value={draft.nextDueDate ?? ''}
                                    onChange={(e) => onField('nextDueDate', e.target.value || null)}
                                    disabled={saving}
                                />
                            </label>
                        </div>

                        <label className="block space-y-1">
                            <div className="text-xs text-gray-500">Next Action</div>
                            <input
                                className="w-full rounded border p-2 text-sm"
                                value={draft.nextAction}
                                onChange={(e) => onField('nextAction', e.target.value)}
                                disabled={saving}
                            />
                        </label>

                        <label className="block space-y-1">
                            <div className="text-xs text-gray-500">Blocked Reason</div>
                            <input
                                className="w-full rounded border p-2 text-sm"
                                value={draft.blockedReason}
                                onChange={(e) => onField('blockedReason', e.target.value)}
                                disabled={saving}
                            />
                        </label>

                        <div className="rounded-xl border bg-gray-50 p-3 space-y-2">
                            <div className="text-xs font-medium text-gray-700">Decision / Evidence (기록)</div>

                            <label className="block space-y-1">
                                <div className="text-xs text-gray-500">Decision Note</div>
                                <textarea
                                    className="w-full rounded border p-2 text-sm min-h-[70px] bg-white"
                                    value={draft.decisionNote}
                                    onChange={(e) => onField('decisionNote', e.target.value)}
                                    disabled={saving}
                                />
                            </label>

                            <label className="block space-y-1">
                                <div className="text-xs text-gray-500">Evidence URL</div>
                                <input
                                    className="w-full rounded border p-2 text-sm bg-white"
                                    value={draft.evidenceUrl}
                                    onChange={(e) => onField('evidenceUrl', e.target.value)}
                                    disabled={saving}
                                    placeholder="https://..."
                                />
                            </label>
                        </div>

                        {mode === 'create' ? (
                            <div className="flex justify-end gap-2">
                                <button
                                    className="text-xs rounded px-3 py-2 border bg-white hover:bg-gray-50"
                                    onClick={onClose}
                                    disabled={saving}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="text-xs rounded px-3 py-2 bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-50"
                                    onClick={saveCreate}
                                    disabled={saving || !canSave}
                                >
                                    Create
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <div className="flex justify-end gap-2">
                                    <button
                                        className="text-xs rounded px-3 py-2 border bg-white hover:bg-gray-50"
                                        onClick={saveEdit}
                                        disabled={saving || !canSave}
                                    >
                                        Save (fields)
                                    </button>
                                </div>

                                <div className="rounded-xl border p-3 space-y-2">
                                    <div className="text-xs font-medium text-gray-700">Status Update (기록)</div>
                                    <label className="block space-y-1">
                                        <div className="text-xs text-gray-500">Note</div>
                                        <input
                                            className="w-full rounded border p-2 text-sm"
                                            value={statusNote}
                                            onChange={(e) => setStatusNote(e.target.value)}
                                            disabled={saving}
                                            placeholder="이번 상태 변경 사유/메모"
                                        />
                                    </label>

                                    <div className="flex justify-end">
                                        <button
                                            className="text-xs rounded px-3 py-2 bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-50"
                                            onClick={changeStatus}
                                            disabled={saving}
                                        >
                                            Apply Status Update
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {detailQ.isError ? (
                            <div className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                                로드 실패: {String((detailQ.error as any)?.message ?? detailQ.error)}
                            </div>
                        ) : null}
                    </div>
                )}
            </div>
        </div>
    );
}
